"use client";

import { useState } from "react";
import dynamic from "next/dynamic";
import { ChevronDownIcon } from "@heroicons/react/20/solid";

import type { OpeningHour } from "@/graphql/sdk/__generated__";
import { cn } from "@/helpers/className";
import { dayNumberToString } from "@/helpers/openingHours";
import { AllowedLanguages } from "@/types/generic";

interface OpeningHoursProps {
  locale: AllowedLanguages;
  opening_hours: OpeningHour[];
}

const IsOpenMessageNoSSR = dynamic(
  () => import("@/components/molecules/IsOpenMessage"),
  { ssr: false },
);

export const OpeningHours = ({ opening_hours, locale }: OpeningHoursProps) => {
  const today = new Date().getDay();

  const [isToggleOpen, setIsToggleOpen] = useState(false);

  return (
    <div className="">
      <div className="flex items-center justify-between">
        <IsOpenMessageNoSSR opening_hours={opening_hours} locale={locale} />
        <button
          type="button"
          className="p-2"
          onClick={() => setIsToggleOpen(!isToggleOpen)}
        >
          <ChevronDownIcon
            className={cn(
              "-mr-2 h-8 w-8 text-gray-500",
              isToggleOpen && "rotate-180",
            )}
          />
        </button>
      </div>

      <div className={cn("min-w-full py-2", !isToggleOpen && "hidden")}>
        <hr className="my-2" />
        {opening_hours.map(({ day, hours }) => (
          <div
            key={day}
            className={cn(
              "text-md grid-cols-2 py-1.5 text-black sm:grid",
              day === today ? "font-bold" : "",
            )}
          >
            <div>
              <span
                className={cn(
                  "font-medium lg:font-normal",
                  day === today ? "font-bold lg:font-bold" : "",
                )}
              >
                {dayNumberToString(day, locale)}
              </span>
              <dl>
                <dd className="mt-1 truncate sm:hidden">
                  {hours
                    ?.map(
                      ({ opening_hour, closing_hour }) =>
                        `${opening_hour} - ${closing_hour}`,
                    )
                    .join(", ")}
                </dd>
              </dl>
            </div>
            <div className="hidden text-right sm:table-cell">
              {hours
                ?.map(
                  ({ opening_hour, closing_hour }) =>
                    `${opening_hour} - ${closing_hour}`,
                )
                .join(", ")}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
